/**
 * Zod schemas for API request validation. Reused on web (forms) and api (handlers).
 */
import { z } from "zod";

export const PROJECT_TYPES = ["client", "internal"] as const;
export const PROJECT_STATUSES = ["discovery", "active", "paused", "completed", "archived"] as const;
export const PROJECT_HEALTHS = ["green", "yellow", "red", "unknown"] as const;
export const PROJECT_LINK_KINDS = [
  "mural",
  "drive",
  "plane",
  "client_site",
  "notes",
  "figma",
  "github",
  "gitlab",
  "other",
] as const;

export const projectCreateSchema = z
  .object({
    name: z.string().min(2).max(120),
    slug: z
      .string()
      .min(2)
      .max(60)
      .regex(/^[a-z0-9-]+$/, "slug solo puede tener a-z 0-9 y guiones"),
    type: z.enum(PROJECT_TYPES),
    status: z.enum(PROJECT_STATUSES).optional().default("active"),
    health: z.enum(PROJECT_HEALTHS).optional().default("unknown"),
    description: z.string().max(2000).optional(),
    clientCompanyId: z.string().optional(),
    mattermostChannelId: z.string().optional(),
    planeProjectId: z.string().optional(),
    planeWorkspaceSlug: z.string().optional(),
    startedAt: z.number().int().optional(),
    targetEndAt: z.number().int().optional(),
  })
  .refine((d) => d.type === "internal" || !!d.clientCompanyId, {
    message: "Proyectos cliente requieren una empresa cliente",
    path: ["clientCompanyId"],
  });

export const projectUpdateSchema = z.object({
  name: z.string().min(2).max(120).optional(),
  type: z.enum(PROJECT_TYPES).optional(),
  status: z.enum(PROJECT_STATUSES).optional(),
  health: z.enum(PROJECT_HEALTHS).optional(),
  healthNote: z.string().max(500).optional(),
  description: z.string().max(2000).optional(),
  clientCompanyId: z.string().optional(),
  mattermostChannelId: z.string().optional(),
  planeProjectId: z.string().optional(),
  planeWorkspaceSlug: z.string().optional(),
  startedAt: z.number().int().optional(),
  targetEndAt: z.number().int().optional(),
});

export const projectLinkCreateSchema = z.object({
  kind: z.enum(PROJECT_LINK_KINDS),
  label: z.string().min(1).max(80),
  url: z.string().url(),
  hasPreview: z.boolean().default(true),
});

export const projectMemberAddSchema = z.object({
  userId: z.string(),
  roleInProject: z.enum(["lead", "collaborator", "observer"]).optional().default("collaborator"),
});

// Solo dos preguntas mapean tareas individuales (items con
// proyecto/ticket/menciones). Las demás son texto libre.
export const STANDUP_ITEM_SECTIONS = ["yesterday", "today"] as const;
export const STANDUP_SECTIONS = [
  "yesterday",
  "yesterday_blockers",
  "general_difficulties",
  "today",
  "today_blockers",
  "tooling_friction",
  "comments",
] as const;

export const STANDUP_SECTION_LABELS: Record<(typeof STANDUP_SECTIONS)[number], string> = {
  yesterday: "¿Qué tareas realizaste ayer?",
  yesterday_blockers: "¿Hubo algún obstáculo que impidiera completar las tareas asignadas ayer?",
  general_difficulties:
    "¿Encontraste alguna dificultad técnica o de otro tipo al realizar tus tareas?",
  today: "¿Qué tareas tienes previstas para hoy?",
  today_blockers: "¿Tienes alguna duda o bloqueo que te impida avanzar en alguna tarea de hoy?",
  tooling_friction:
    "¿Hay alguna herramienta o recurso que sientas que te está dificultando el avance?",
  comments: "Comentarios",
};

/**
 * Snapshot de un ticket Plane que se referencia en un item del standup.
 * Snapshot total (título + url + sequence) para que el item se siga
 * leyendo si el ticket cambia o se borra después.
 */
export const standupItemTicketSchema = z.object({
  planeProjectId: z.string(),
  planeIssueId: z.string(),
  issueTitleSnapshot: z.string().max(500),
  issueUrl: z.string().url(),
  sequenceId: z.number().int().optional(),
  projectIdentifier: z.string().max(40).optional(),
});

/**
 * Item del standup. Texto libre + proyecto del panel + N tickets de Plane
 * + N menciones a compañeros. Las imágenes viven en attachments aparte.
 */
export const standupItemSchema = z.object({
  /**
   * ID server. Solo se manda al editar — el backend lo usa para hacer
   * diff (update vs create vs delete por items presentes). En create
   * (POST nuevo standup) se ignora si llega.
   */
  id: z.string().optional(),
  section: z.enum(STANDUP_ITEM_SECTIONS),
  position: z.number().int().min(0).default(0),
  text: z.string().max(2000).optional(),
  panelProjectId: z.string().optional(),
  tickets: z.array(standupItemTicketSchema).max(20).default([]),
  /** IDs de users del panel que se mencionan en este item. */
  mentionUserIds: z.array(z.string()).max(20).default([]),
});

export type StandupItemTicketInput = z.input<typeof standupItemTicketSchema>;

export const standupCreateSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  /** Items estructurados — solo para yesterday y today_blockers. */
  items: z.array(standupItemSchema).max(200).default([]),
  /** Preguntas free-text. Vacío permitido — el panel acepta "no tengo
   * nada para esta sección" sin obligar a escribir N/A. */
  yesterdayBlockers: z.string().max(2000).optional(),
  generalDifficulties: z.string().max(2000).optional(),
  todayBlockers: z.string().max(2000).optional(),
  toolingFriction: z.string().max(2000).optional(),
  comments: z.string().max(2000).optional(),
  /** Usuarios mencionados con @ en cualquier free-text. Únicos. */
  mentionedUserIds: z.array(z.string()).max(50).default([]),
  postToMattermost: z.boolean().default(true),
  crossPostToProjects: z.boolean().default(true),
  postCommentsToPlane: z.boolean().default(true),
});

export type StandupItemInput = z.input<typeof standupItemSchema>;

export const dayOffRequestCreateSchema = z
  .object({
    startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    kind: z.enum(["vacation", "sick", "personal", "unpaid"]),
    reason: z.string().max(500).optional(),
  })
  .refine((d) => d.endDate >= d.startDate, {
    message: "endDate debe ser >= startDate",
    path: ["endDate"],
  });

export const dayOffDecisionSchema = z.object({
  status: z.enum(["approved", "rejected"]),
  approverComment: z.string().max(500).optional(),
});

// ─── Calendar events ────────────────────────────────────────────────────────

// CALENDAR_EVENT_KINDS está exportado desde schema.ts. Lo re-exportamos
// aquí para que el cliente lo importe junto con los demás constantes
// de validación.
import { CALENDAR_EVENT_KINDS, CALENDAR_RECURRENCES } from "./schema";
export { CALENDAR_EVENT_KINDS, CALENDAR_RECURRENCES };

const TIME_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/;

export const calendarEventCreateSchema = z
  .object({
    startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    startTime: z.string().regex(TIME_REGEX).nullable().optional(),
    endTime: z.string().regex(TIME_REGEX).nullable().optional(),
    kind: z.enum(CALENDAR_EVENT_KINDS),
    blocksWork: z.boolean().default(false),
    name: z.string().min(1).max(160),
    notes: z.string().max(1000).optional(),
    userId: z.string().nullable().optional(),
    externalId: z.string().optional(),
    /** iCalUID canónico (RFC 5545). Lo pasa el picker de "Basar en evento
     * recurrente" para que el dedup at-read funcione aunque el `externalId`
     * varíe entre organizer y attendees del recurrente. */
    externalIcalUid: z.string().optional(),
    externalUrl: z.string().url().optional(),
    /** Link de videoconferencia (Google Meet, Zoom, etc.). Para eventos
     *  sincronizados de Google lo llena el sync; manualmente se permite
     *  pegar cualquier URL en este campo. */
    externalMeetUrl: z.string().url().optional(),
    recurrence: z.enum(CALENDAR_RECURRENCES).nullable().optional(),
    recurrenceDays: z.array(z.number().int().min(0).max(6)).nullable().optional(),
    recurrenceUntil: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .nullable()
      .optional(),
  })
  .refine((e) => e.endDate >= e.startDate, {
    message: "endDate debe ser >= startDate",
    path: ["endDate"],
  })
  .refine((e) => !e.recurrenceUntil || e.recurrenceUntil >= e.startDate, {
    message: "recurrenceUntil debe ser >= startDate",
    path: ["recurrenceUntil"],
  });

export const calendarEventUpdateSchema = z
  .object({
    startDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
    endDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
    startTime: z.string().regex(TIME_REGEX).nullable().optional(),
    endTime: z.string().regex(TIME_REGEX).nullable().optional(),
    kind: z.enum(CALENDAR_EVENT_KINDS).optional(),
    blocksWork: z.boolean().optional(),
    name: z.string().min(1).max(160).optional(),
    notes: z.string().max(1000).nullable().optional(),
    externalId: z.string().nullable().optional(),
    externalUrl: z.string().url().nullable().optional(),
    externalMeetUrl: z.string().url().nullable().optional(),
    recurrence: z.enum(CALENDAR_RECURRENCES).nullable().optional(),
    recurrenceDays: z.array(z.number().int().min(0).max(6)).nullable().optional(),
    recurrenceUntil: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .nullable()
      .optional(),
  })
  .refine((e) => !e.startDate || !e.endDate || e.endDate >= e.startDate, {
    message: "endDate debe ser >= startDate",
    path: ["endDate"],
  });

export type CalendarEventCreateInput = z.input<typeof calendarEventCreateSchema>;

export const googleCalendarSyncToggleSchema = z.object({
  enabled: z.boolean(),
});
export type GoogleCalendarSyncToggleInput = z.input<typeof googleCalendarSyncToggleSchema>;

// ─── Event sessions ─────────────────────────────────────────────────────────

import { EVENT_SESSION_STATUSES } from "./schema";
export { EVENT_SESSION_STATUSES };

export const eventSessionUpsertSchema = z.object({
  description: z.string().max(20000).nullable().optional(),
  muralUrl: z.string().url().nullable().optional(),
  muralEmbedUrl: z.string().url().nullable().optional(),
  recordingUrl: z.string().url().nullable().optional(),
  transcriptUrl: z.string().url().nullable().optional(),
  geminiNotesUrl: z.string().url().nullable().optional(),
  status: z.enum(EVENT_SESSION_STATUSES).optional(),
});
export type EventSessionUpsertInput = z.input<typeof eventSessionUpsertSchema>;

// ─── Finanzas ───────────────────────────────────────────────────────────────

export const ACCOUNT_TYPES = ["bank", "credit_card", "cash", "loan", "other"] as const;
export const PAYMENT_METHODS = ["transfer", "card_credit", "card_debit", "cash"] as const;
export const RECURRENCES = ["none", "weekly", "monthly", "quarterly", "yearly"] as const;

/**
 * Preset por tipo de cuenta — sirve para precargar el modal de cuenta
 * cuando el usuario cambia el `type`. Se duplica este shape en la
 * migración 0042 (backfill), mantener en sync si cambia.
 */
export const PAYMENT_METHODS_BY_ACCOUNT_TYPE: Record<
  (typeof ACCOUNT_TYPES)[number],
  {
    allowed: ReadonlyArray<(typeof PAYMENT_METHODS)[number]>;
    default: (typeof PAYMENT_METHODS)[number] | null;
  }
> = {
  bank: { allowed: ["transfer"], default: "transfer" },
  credit_card: { allowed: ["card_credit"], default: "card_credit" },
  cash: { allowed: ["cash"], default: "cash" },
  loan: { allowed: ["transfer"], default: "transfer" },
  other: { allowed: [...PAYMENT_METHODS], default: null },
};

export const accountCreateSchema = z
  .object({
    name: z.string().min(1).max(80),
    type: z.enum(ACCOUNT_TYPES).default("bank"),
    bankName: z.string().max(60).optional(),
    creditLimitCents: z.number().int().min(0).optional(),
    currency: z.string().length(3).default("MXN"),
    openingBalanceCents: z.number().int().default(0),
    notes: z.string().max(500).optional(),
    // Métodos de pago permitidos al usar esta cuenta en el modal de tx.
    // Mínimo 1. Default = todos los soportados (caso "other" no-loan).
    allowedPaymentMethods: z
      .array(z.enum(PAYMENT_METHODS))
      .min(1)
      .default([...PAYMENT_METHODS]),
    defaultPaymentMethod: z.enum(PAYMENT_METHODS).nullable().optional(),
    // Campos loan
    loanOriginalCents: z.number().int().min(0).optional(),
    loanMonthlyPaymentCents: z.number().int().min(0).optional(),
    loanInterestRateBps: z.number().int().min(0).max(100000).optional(),
    loanTotalPayments: z.number().int().min(1).max(360).optional(),
    loanPaymentDay: z.number().int().min(1).max(31).optional(),
    loanFolio: z.string().max(40).optional(),
    loanNextPaymentDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
  })
  .refine(
    (a) => a.type !== "credit_card" || (a.creditLimitCents !== undefined && a.creditLimitCents > 0),
    {
      message: "credit_card requiere creditLimitCents > 0",
      path: ["creditLimitCents"],
    },
  )
  .refine((a) => a.type !== "loan" || (a.loanMonthlyPaymentCents ?? 0) > 0, {
    message: "loan requiere pago mensual > 0",
    path: ["loanMonthlyPaymentCents"],
  })
  .refine(
    (a) => !a.defaultPaymentMethod || a.allowedPaymentMethods.includes(a.defaultPaymentMethod),
    {
      message: "el default debe estar entre los métodos permitidos",
      path: ["defaultPaymentMethod"],
    },
  );

export const accountUpdateSchema = z
  .object({
    name: z.string().min(1).max(80).optional(),
    type: z.enum(ACCOUNT_TYPES).optional(),
    bankName: z.string().max(60).nullable().optional(),
    creditLimitCents: z.number().int().min(0).nullable().optional(),
    currency: z.string().length(3).optional(),
    openingBalanceCents: z.number().int().optional(),
    active: z.boolean().optional(),
    notes: z.string().max(500).nullable().optional(),
    allowedPaymentMethods: z.array(z.enum(PAYMENT_METHODS)).min(1).optional(),
    defaultPaymentMethod: z.enum(PAYMENT_METHODS).nullable().optional(),
    loanOriginalCents: z.number().int().min(0).nullable().optional(),
    loanMonthlyPaymentCents: z.number().int().min(0).nullable().optional(),
    loanInterestRateBps: z.number().int().min(0).max(100000).nullable().optional(),
    loanTotalPayments: z.number().int().min(1).max(360).nullable().optional(),
    loanPaymentDay: z.number().int().min(1).max(31).nullable().optional(),
    loanFolio: z.string().max(40).nullable().optional(),
    loanNextPaymentDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .nullable()
      .optional(),
  })
  .refine(
    (a) =>
      !a.defaultPaymentMethod ||
      !a.allowedPaymentMethods ||
      a.allowedPaymentMethods.includes(a.defaultPaymentMethod),
    {
      message: "el default debe estar entre los métodos permitidos",
      path: ["defaultPaymentMethod"],
    },
  );

export const categoryCreateSchema = z.object({
  name: z.string().min(1).max(80),
  kind: z.enum(["ingreso", "gasto"]),
  group: z.string().max(40).optional(),
  ivaApplicable: z.boolean().default(true),
});

export const categoryUpdateSchema = z.object({
  name: z.string().min(1).max(80).optional(),
  kind: z.enum(["ingreso", "gasto"]).optional(),
  group: z.string().max(40).nullable().optional(),
  ivaApplicable: z.boolean().optional(),
  active: z.boolean().optional(),
});

export const transactionCreateSchema = z
  .object({
    accountId: z.string(),
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    kind: z.enum(["ingreso", "gasto", "transferencia"]),
    paymentMethod: z.enum(PAYMENT_METHODS).optional(),
    recurrence: z.enum(RECURRENCES).default("none"),
    categoryId: z.string().optional(),
    companyId: z.string().optional(),
    counterparty: z.string().max(120).optional(),
    title: z.string().max(120).optional(),
    description: z.string().max(500).optional(),
    subtotalCents: z.number().int(),
    ivaCents: z.number().int().min(0).default(0),
    totalCents: z.number().int(),
    invoiceStatus: z
      .enum(["facturado", "deducible_sin_factura", "pendiente", "no_aplica", "olvidado"])
      .default("pendiente"),
    invoiceUuid: z.string().optional(),
    paymentStatus: z.enum(["cobrado", "pagado", "pendiente"]).default("pendiente"),
    paymentDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
    affectsTotals: z.boolean().default(true),
    isrApplicable: z.boolean().default(true),
    documentedExternally: z.boolean().default(false),
    finDocumentId: z.string().optional(),
    projectId: z.string().optional(),
    notes: z.string().max(1000).optional(),
    // Asimilado a salarios (Art. 96 LISR). Solo aplica cuando kind=gasto.
    // total_cents en este caso = bruto deducible; isr_retenido_cents es
    // informativo (lo entera la empresa al SAT en una tx aparte).
    isAsimilado: z.boolean().default(false),
    beneficiaryName: z.string().max(120).optional(),
    beneficiaryRfc: z.string().max(20).optional(),
    beneficiaryCurp: z.string().max(20).optional(),
    isrRetenidoCents: z.number().int().min(0).optional(),
    payrollUserId: z.string().optional(),
    // Pago al SAT: el bruto es lo que sale del banco. El desglose
    // (IVA/ISR/otros) lo hace splits con categorías "Impuestos — *".
    isSatPayment: z.boolean().default(false),
    satLineaCaptura: z.string().max(40).optional(),
    satPeriod: z
      .string()
      .regex(/^\d{4}-\d{2}$/)
      .optional(),
  })
  .refine((t) => t.subtotalCents + t.ivaCents === t.totalCents, {
    message: "subtotal + iva debe igualar total",
    path: ["totalCents"],
  });

export const transactionUpdateSchema = z.object({
  accountId: z.string().optional(),
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  kind: z.enum(["ingreso", "gasto", "transferencia"]).optional(),
  paymentMethod: z.enum(PAYMENT_METHODS).nullable().optional(),
  recurrence: z.enum(RECURRENCES).optional(),
  categoryId: z.string().optional(),
  companyId: z.string().nullable().optional(),
  counterparty: z.string().max(120).optional(),
  title: z.string().max(120).nullable().optional(),
  description: z.string().max(500).optional(),
  subtotalCents: z.number().int().optional(),
  ivaCents: z.number().int().min(0).optional(),
  totalCents: z.number().int().optional(),
  invoiceStatus: z
    .enum(["facturado", "deducible_sin_factura", "pendiente", "no_aplica", "olvidado"])
    .optional(),
  invoiceUuid: z.string().optional(),
  paymentStatus: z.enum(["cobrado", "pagado", "pendiente"]).optional(),
  paymentDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  affectsTotals: z.boolean().optional(),
  isrApplicable: z.boolean().optional(),
  documentedExternally: z.boolean().optional(),
  finDocumentId: z.string().nullable().optional(),
  projectId: z.string().optional(),
  notes: z.string().max(1000).optional(),
  isAsimilado: z.boolean().optional(),
  beneficiaryName: z.string().max(120).nullable().optional(),
  beneficiaryRfc: z.string().max(20).nullable().optional(),
  beneficiaryCurp: z.string().max(20).nullable().optional(),
  isrRetenidoCents: z.number().int().min(0).nullable().optional(),
  payrollUserId: z.string().nullable().optional(),
  isSatPayment: z.boolean().optional(),
  satLineaCaptura: z.string().max(40).nullable().optional(),
  satPeriod: z
    .string()
    .regex(/^\d{4}-\d{2}$/)
    .nullable()
    .optional(),
});

// Tax filings — declaraciones mensuales del SAT, una por (period, type).
// El recibo SAT separa ISR en 3 conceptos: propio (RESICO PM),
// retenciones asimilados (Art. 96) y retenciones salarios (Cap. I).
export const taxFilingUpsertSchema = z.object({
  period: z.string().regex(/^\d{4}-\d{2}$/),
  taxType: z.enum(["isr_propio", "isr_asimilados", "isr_salarios", "iva", "otro"]),
  systemCalcCents: z.number().int().default(0),
  paidCents: z.number().int().min(0).default(0),
  paidAt: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  lineaCaptura: z.string().max(40).nullable().optional(),
  transactionId: z.string().nullable().optional(),
  notes: z.string().max(500).nullable().optional(),
});

export const transferCreateSchema = z.object({
  fromAccountId: z.string(),
  toAccountId: z.string(),
  amountCents: z.number().int().positive(),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  affectsTotals: z.boolean().default(true),
  notes: z.string().max(500).optional(),
});

// Sub-ingresos / sub-gastos: cada split del padre. El `id` es opcional al
// hacer PUT bulk-replace (si no viene, se crea; si viene, se actualiza).
// La invariante sum(total) == padre.total se valida en el handler.
export const transactionSplitInputSchema = z.object({
  id: z.string().optional(),
  categoryId: z.string().nullable().optional(),
  projectId: z.string().nullable().optional(),
  companyId: z.string().nullable().optional(),
  description: z.string().max(500).nullable().optional(),
  subtotalCents: z.number().int(),
  ivaCents: z.number().int().min(0).default(0),
  totalCents: z.number().int(),
  notes: z.string().max(500).nullable().optional(),
  position: z.number().int().min(0).default(0),
});

export const transactionSplitsReplaceSchema = z.object({
  splits: z.array(transactionSplitInputSchema).max(50),
});

export type TransactionSplitInput = z.input<typeof transactionSplitInputSchema>;

export const snapshotConsolidateSchema = z.object({
  upToPeriod: z.string().regex(/^\d{4}-\d{2}$/), // genera snapshots de todos los meses ≤ upToPeriod
  archiveTransactions: z.boolean().default(false), // por ahora false (mantener ambos)
});

export const importMappingSchema = z.object({
  // Columnas del CSV/XLSX mapeadas a campos.
  date: z.string().min(1), // nombre de columna con la fecha
  description: z.string().min(1),
  // Monto: o columna única (positivo=ingreso, negativo=gasto), o dos columnas
  // separadas (cargo / abono).
  amount: z.string().optional(),
  cargoCol: z.string().optional(),
  abonoCol: z.string().optional(),
  // Formato de fecha del archivo. Default "DD/MM/YYYY". Acepta también
  // "YYYY-MM-DD", "MM/DD/YYYY".
  dateFormat: z.string().optional(),
});

export const importTemplateCreateSchema = z.object({
  name: z.string().min(1).max(60),
  accountId: z.string().optional(),
  mapping: importMappingSchema,
  notes: z.string().max(500).optional(),
});

/**
 * Bulk import: el cliente parsea PDFs/CSVs y manda un payload con todas las
 * transacciones por cuenta. El backend hace insert atómico + reconciliación
 * de transferencias internas.
 *
 * `internalRef` es un identificador opcional que permite al cliente sugerir
 * matches (ej. "konfio:guía:3226238" → el server sabe que esa fila es la
 * misma que un T17 SPEI del BBVA).
 */
export const bulkImportRowSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  description: z.string().max(500).optional(),
  kind: z.enum(["ingreso", "gasto"]),
  amountCents: z.number().int().positive(),
  ivaCents: z.number().int().min(0).default(0),
  // Si el parser ya determinó que es transfer interno (ej. "TRASPASO SPEI
  // INBURED" con RFC NOB230522884), lo marca aquí. El backend no necesita
  // hacer su propia reconciliación porque ya viene resuelta.
  internalTransferHint: z
    .object({
      kind: z.enum(["transfer_out", "transfer_in"]),
      // Pista para matchear con el otro lado: amount + date + counterparty
      // hash que el cliente computa.
      matchKey: z.string().optional(),
    })
    .optional(),
  // Campos para detección post-insert. Originales del estado de cuenta.
  rawCounterparty: z.string().max(120).optional(),
  rawCode: z.string().max(40).optional(),
});

export const bulkImportSchema = z.object({
  imports: z
    .array(
      z.object({
        accountId: z.string(),
        bankLabel: z.string().max(40), // "bbva", "inbursa", "konfio", etc.
        rows: z.array(bulkImportRowSchema).max(2000),
      }),
    )
    .min(1)
    .max(200), // hasta 200 archivos por batch — cubre carga histórica multi-año
  defaults: z.object({
    affectsTotals: z.boolean().default(true),
    isrApplicable: z.boolean().default(true),
    invoiceStatus: z
      .enum(["facturado", "deducible_sin_factura", "pendiente", "no_aplica", "olvidado"])
      .default("pendiente"),
  }),
});

export type BulkImportInput = z.input<typeof bulkImportSchema>;
export type BulkImportRow = z.input<typeof bulkImportRowSchema>;

export const importRunSchema = z.object({
  accountId: z.string(),
  affectsTotals: z.boolean().default(false), // historicos default false
  isrApplicable: z.boolean().default(true),
  defaultCategoryIngresoId: z.string().optional(),
  defaultCategoryGastoId: z.string().optional(),
  defaultInvoiceStatus: z
    .enum(["facturado", "deducible_sin_factura", "pendiente", "no_aplica", "olvidado"])
    .default("pendiente"),
  rows: z
    .array(
      z.object({
        date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
        description: z.string().max(500).optional(),
        kind: z.enum(["ingreso", "gasto"]),
        amountCents: z.number().int(), // monto absoluto (sin signo)
      }),
    )
    .min(1)
    .max(2000),
});

export type ImportMapping = z.input<typeof importMappingSchema>;
export type ImportTemplateCreateInput = z.input<typeof importTemplateCreateSchema>;
export type ImportRunInput = z.input<typeof importRunSchema>;

export const transferUpdateSchema = z.object({
  fromAccountId: z.string().optional(),
  toAccountId: z.string().optional(),
  amountCents: z.number().int().positive().optional(),
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  affectsTotals: z.boolean().optional(),
  notes: z.string().max(500).nullable().optional(),
});

const hypotheticalEntrySchema = z.object({
  kind: z.enum(["ingreso", "gasto"]),
  subtotalCents: z.number().int(),
  ivaCents: z.number().int().min(0).default(0),
  paymentDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  facturado: z.boolean().default(true), // afecta IVA acreditable (solo para gastos)
});

export const isrSimulationSchema = z.object({
  period: z.string().regex(/^\d{4}-\d{2}$/), // YYYY-MM
  hypotheticals: z.array(hypotheticalEntrySchema).max(20).default([]),
});

export type AccountCreateInput = z.input<typeof accountCreateSchema>;
export type AccountUpdateInput = z.input<typeof accountUpdateSchema>;
export type CategoryCreateInput = z.input<typeof categoryCreateSchema>;
export type CategoryUpdateInput = z.input<typeof categoryUpdateSchema>;
export type TransactionUpdateInput = z.input<typeof transactionUpdateSchema>;
export type TransferCreateInput = z.input<typeof transferCreateSchema>;
export type AccountType = (typeof ACCOUNT_TYPES)[number];
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];
export type Recurrence = (typeof RECURRENCES)[number];

// ─── Evaluations v2 ──────────────────────────────────────────────────────────

export const EVAL_LEVELS = ["client_user", "organizational", "team", "project"] as const;
export const EVAL_LEVEL_LABELS: Record<(typeof EVAL_LEVELS)[number], string> = {
  client_user: "Cliente / Usuario",
  organizational: "Organizacional",
  team: "Equipo",
  project: "Proyecto",
};

// 4 niveles de visibilidad. Matriz de acceso:
//   everyone   → admin, finanzas, lead, member ven; admin/lead capturan
//   lead       → admin, finanzas, lead ven y capturan
//   finanzas   → admin, finanzas ven y capturan
//   admin_only → solo admin ve y captura (datos sensibles)
export const EVAL_VISIBILITIES = ["everyone", "lead", "finanzas", "admin_only"] as const;
export const EVAL_VISIBILITY_LABELS: Record<(typeof EVAL_VISIBILITIES)[number], string> = {
  everyone: "Todos",
  lead: "Líderes y arriba",
  finanzas: "Finanzas y arriba",
  admin_only: "Solo admin",
};

export const EVAL_RECURRENCES = [
  "monthly",
  "biweekly",
  "quarterly",
  "semester",
  "annual",
  "on_demand",
  "per_project_close",
] as const;
export const EVAL_RECURRENCE_LABELS: Record<(typeof EVAL_RECURRENCES)[number], string> = {
  monthly: "Mensual",
  biweekly: "Quincenal",
  quarterly: "Trimestral",
  semester: "Semestral",
  annual: "Anual",
  on_demand: "Bajo demanda",
  per_project_close: "Al cierre del proyecto",
};

export const APPLIES_TO_PROJECTS_OPTS = ["all", "none", "per_project_opt_in"] as const;
export const APPLIES_TO_PROJECTS_LABELS: Record<(typeof APPLIES_TO_PROJECTS_OPTS)[number], string> =
  {
    all: "Todos los proyectos",
    none: "Ningún proyecto",
    per_project_opt_in: "Solo si el proyecto lo activa",
  };

export const METRIC_DIRECTIONS = ["higher_better", "lower_better", "range"] as const;
export const METRIC_DIRECTION_LABELS: Record<(typeof METRIC_DIRECTIONS)[number], string> = {
  higher_better: "Mejor más alto",
  lower_better: "Mejor más bajo",
  range: "Rango óptimo",
};

export const ACTIVATION_DEF_KINDS = ["kpi", "questionnaire", "assessment"] as const;

export const KPI_NATURES = ["quantitative", "qualitative", "mixed"] as const;
export const QUESTIONNAIRE_NATURES = ["quantitative", "qualitative", "mixed"] as const;
export const QUESTIONNAIRE_NATURE_LABELS: Record<(typeof QUESTIONNAIRE_NATURES)[number], string> = {
  quantitative: "Cuantitativo (escalas)",
  qualitative: "Cualitativo (texto libre)",
  mixed: "Mixto",
};

export const ASSESSMENT_SCORING_METHODS = ["weighted_average", "worst_signal", "custom"] as const;
export const ASSESSMENT_SCORING_METHOD_LABELS: Record<
  (typeof ASSESSMENT_SCORING_METHODS)[number],
  string
> = {
  weighted_average: "Promedio ponderado",
  worst_signal: "Peor señal manda",
  custom: "Fórmula personalizada",
};

/**
 * Item de plantilla — cuantitativo (con escala numérica) o cualitativo (texto
 * libre). Discriminated union para que el form pueda renderizar el control
 * correcto.
 */
export const evaluationItemSchema = z.discriminatedUnion("kind", [
  z.object({
    id: z.string().min(1),
    kind: z.literal("quant"),
    prompt: z.string().min(1).max(500),
    scale: z.object({
      min: z.number(),
      max: z.number(),
      minLabel: z.string().max(40).optional(),
      maxLabel: z.string().max(40).optional(),
    }),
    required: z.boolean().default(false),
  }),
  z.object({
    id: z.string().min(1),
    kind: z.literal("qual"),
    prompt: z.string().min(1).max(500),
    placeholder: z.string().max(200).optional(),
    required: z.boolean().default(false),
  }),
]);

export type EvaluationItem = z.input<typeof evaluationItemSchema>;

export const questionnaireCreateSchema = z.object({
  name: z.string().min(2).max(160),
  level: z.enum(EVAL_LEVELS),
  visibility: z.enum(EVAL_VISIBILITIES).default("everyone"),
  description: z.string().max(2000).optional(),
  items: z.array(evaluationItemSchema).min(1).max(40),
  recurrence: z.enum(EVAL_RECURRENCES).default("on_demand"),
  isAnonymous: z.boolean().default(false),
  appliesToProjects: z.enum(APPLIES_TO_PROJECTS_OPTS).default("none"),
  nature: z.enum(QUESTIONNAIRE_NATURES).default("mixed"),
});

export const questionnaireUpdateSchema = questionnaireCreateSchema
  .partial()
  .extend({ active: z.boolean().optional() });

export const questionnaireInstanceOpenSchema = z.object({
  questionnaireId: z.string(),
  period: z.string().min(2).max(20),
  projectId: z.string().optional(),
  parentInstanceId: z.string().optional(),
});

export const evaluationAnswerValueSchema = z.union([z.number(), z.string().max(2000)]);

export const questionnaireResponseSchema = z.object({
  answers: z.record(
    z.string(),
    z.object({
      value: evaluationAnswerValueSchema,
      comment: z.string().max(1000).optional(),
    }),
  ),
});

export type QuestionnaireCreateInput = z.input<typeof questionnaireCreateSchema>;
export type QuestionnaireUpdateInput = z.input<typeof questionnaireUpdateSchema>;
export type QuestionnaireInstanceOpenInput = z.input<typeof questionnaireInstanceOpenSchema>;
export type QuestionnaireResponseInput = z.input<typeof questionnaireResponseSchema>;

// ─── KPI Definitions ─────────────────────────────────────────────────────────

export const kpiDefinitionCreateSchema = z.object({
  name: z.string().min(2).max(160),
  description: z.string().max(2000).optional(),
  level: z.enum(EVAL_LEVELS),
  nature: z.enum(KPI_NATURES).default("quantitative"),
  unit: z.string().max(40).optional(),
  direction: z.enum(METRIC_DIRECTIONS).default("higher_better"),
  thresholdExcellent: z.number().optional(),
  thresholdGood: z.number().optional(),
  thresholdImprovable: z.number().optional(),
  thresholdBad: z.number().optional(),
  recurrence: z.enum(EVAL_RECURRENCES).default("monthly"),
  active: z.boolean().default(true),
  visibility: z.enum(EVAL_VISIBILITIES).default("everyone"),
  appliesToProjects: z.enum(APPLIES_TO_PROJECTS_OPTS).default("none"),
  autoSource: z.string().max(80).optional(),
});

export const kpiDefinitionUpdateSchema = kpiDefinitionCreateSchema.partial();

export type KpiDefinitionCreateInput = z.input<typeof kpiDefinitionCreateSchema>;
export type KpiDefinitionUpdateInput = z.input<typeof kpiDefinitionUpdateSchema>;

// ─── KPI Values ──────────────────────────────────────────────────────────────

export const kpiValueUpsertSchema = z.object({
  definitionId: z.string().min(1),
  projectId: z.string().optional(),
  period: z.string().min(2).max(20),
  value: z.number().optional(),
  note: z.string().max(1000).optional(),
});

export type KpiValueUpsertInput = z.input<typeof kpiValueUpsertSchema>;

// ─── Project Activations (KPI/cuestionario/assessment) ───────────────────────

export const projectActivationUpsertSchema = z.object({
  defKind: z.enum(ACTIVATION_DEF_KINDS),
  defId: z.string().min(1),
  active: z.boolean().default(true),
  thresholdExcellentOverride: z.number().nullable().optional(),
  thresholdGoodOverride: z.number().nullable().optional(),
  thresholdImprovableOverride: z.number().nullable().optional(),
  thresholdBadOverride: z.number().nullable().optional(),
});

export type ProjectActivationUpsertInput = z.input<typeof projectActivationUpsertSchema>;

// ─── Assessments (marco compuesto) ───────────────────────────────────────────

export const assessmentInputUpsertSchema = z.object({
  inputKind: z.enum(["kpi", "questionnaire"]),
  inputId: z.string().min(1),
  weight: z.number().min(0).default(1.0),
  required: z.boolean().default(false),
});

export const assessmentCreateSchema = z.object({
  name: z.string().min(2).max(160),
  description: z.string().max(2000).optional(),
  level: z.enum(EVAL_LEVELS),
  scoringMethod: z.enum(ASSESSMENT_SCORING_METHODS).default("weighted_average"),
  customFormula: z.unknown().optional(),
  recurrence: z.enum(EVAL_RECURRENCES).default("monthly"),
  active: z.boolean().default(true),
  visibility: z.enum(EVAL_VISIBILITIES).default("everyone"),
  appliesToProjects: z.enum(APPLIES_TO_PROJECTS_OPTS).default("none"),
  inputs: z.array(assessmentInputUpsertSchema).default([]),
});

export const assessmentUpdateSchema = assessmentCreateSchema.partial();

export type AssessmentInputUpsertInput = z.input<typeof assessmentInputUpsertSchema>;
export type AssessmentCreateInput = z.input<typeof assessmentCreateSchema>;
export type AssessmentUpdateInput = z.input<typeof assessmentUpdateSchema>;

export const COMPANY_STATUSES = ["active", "prospect", "past", "lost"] as const;

export const companyCreateSchema = z.object({
  name: z.string().min(2).max(160),
  status: z.enum(COMPANY_STATUSES).optional().default("prospect"),
  industry: z.string().max(80).optional(),
  size: z.string().max(40).optional(),
  isSupplier: z.boolean().optional().default(false),
  website: z.string().url().optional(),
  notes: z.string().max(2000).optional(),
  ownerId: z.string().optional(),
});

export const companyUpdateSchema = companyCreateSchema.partial();

export type ProjectCreateInput = z.input<typeof projectCreateSchema>;
export type ProjectUpdateInput = z.input<typeof projectUpdateSchema>;
export type CompanyCreateInput = z.input<typeof companyCreateSchema>;
export type CompanyUpdateInput = z.input<typeof companyUpdateSchema>;
export type StandupCreateInput = z.input<typeof standupCreateSchema>;
export type DayOffRequestCreateInput = z.input<typeof dayOffRequestCreateSchema>;
export type TransactionCreateInput = z.input<typeof transactionCreateSchema>;
export type IsrSimulationInput = z.input<typeof isrSimulationSchema>;
export type TaxFilingUpsertInput = z.input<typeof taxFilingUpsertSchema>;
